import { CheckCircle, Loader2, Send } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

interface PublicSubmitPageProps {
	token: string;
}

interface QueueInfo {
	queue: {
		id: number;
		name: string;
		link_count: number;
	};
	can_read: boolean;
	can_write: boolean;
}

/**
 * PublicSubmitPage - Anonymous link submission via public queue token
 *
 * Allows anonymous users to submit links to a queue using a public access token.
 * Used at route: /submit/:token
 */
export default function PublicSubmitPage({ token }: PublicSubmitPageProps) {
	const [url, setUrl] = useState('');
	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [queueInfo, setQueueInfo] = useState<QueueInfo | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [successCount, setSuccessCount] = useState(0);

	// Load queue info on mount
	useEffect(() => {
		async function loadInfo() {
			try {
				setLoading(true);
				setError(null);
				const response = await fetch(`/api/public/q/${token}/info`);
				if (!response.ok) {
					if (response.status === 404) {
						throw new Error('Invalid or expired token');
					}
					const data = await response.json();
					throw new Error(data.error || `Failed to load queue info: ${response.status}`);
				}
				const data = await response.json();
				setQueueInfo(data);
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to load queue information');
			} finally {
				setLoading(false);
			}
		}
		loadInfo();
	}, [token]);

	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			if (!url.trim()) return;

			try {
				setSubmitting(true);
				setError(null);
				const response = await fetch(`/api/public/q/${token}`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ url: url.trim() }),
				});

				if (!response.ok) {
					const data = await response.json();
					throw new Error(data.error || `Submission failed: ${response.status}`);
				}

				// Success!
				setSuccessCount((prev) => prev + 1);
				setUrl('');
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to submit link');
			} finally {
				setSubmitting(false);
			}
		},
		[url, token]
	);

	if (loading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background">
				<Loader2 className="size-8 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (error && !queueInfo) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background p-4">
				<Card className="max-w-md w-full">
					<CardHeader>
						<CardTitle className="text-destructive">Access Denied</CardTitle>
						<CardDescription>{error}</CardDescription>
					</CardHeader>
					<CardContent>
						<p className="text-sm text-muted-foreground">
							This link may be invalid, expired, or the queue may no longer allow submissions.
						</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (!queueInfo?.can_write) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background p-4">
				<Card className="max-w-md w-full">
					<CardHeader>
						<CardTitle>Read-Only Access</CardTitle>
						<CardDescription>
							This queue does not accept public submissions
						</CardDescription>
					</CardHeader>
					<CardContent>
						<p className="text-sm text-muted-foreground">
							You can view links in this queue, but cannot submit new ones.
						</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-background p-4">
			<Card className="max-w-md w-full">
				<CardHeader>
					<CardTitle>Submit to {queueInfo.queue.name}</CardTitle>
					<CardDescription>
						Share a link to be added to this queue ({queueInfo.queue.link_count} links)
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit} className="space-y-4">
						<div>
							<Input
								type="url"
								value={url}
								onChange={(e) => setUrl(e.target.value)}
								placeholder="https://example.com"
								disabled={submitting}
								autoFocus
								required
							/>
						</div>

						{error && (
							<div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
								{error}
							</div>
						)}

						{successCount > 0 && (
							<div className="text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/20 p-3 rounded-md flex items-center gap-2">
								<CheckCircle className="size-4" />
								<span>
									{successCount === 1
										? 'Link submitted successfully!'
										: `${successCount} links submitted!`}
								</span>
							</div>
						)}

						<Button type="submit" className="w-full" disabled={submitting || !url.trim()}>
							{submitting ? (
								<>
									<Loader2 className="size-4 mr-2 animate-spin" />
									Submitting...
								</>
							) : (
								<>
									<Send className="size-4 mr-2" />
									Submit Link
								</>
							)}
						</Button>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}
